/*
 * Worker GPS Trail Dialog.
 *
 * Opened from the "GPS Trail" KPI tile on the admin Worker-Captures page.
 * Renders a Leaflet map showing:
 *   • The park's configured geofence (sky-blue circle, exact radius).
 *   • One numbered marker per worker attendance capture, plotted at the
 *     GPS coordinates that the supervisor's device recorded at the
 *     moment of capture. Colour-coded:
 *       – Emerald: capture was inside the geofence (inside_geofence=true)
 *       – Rose:    capture was outside the geofence
 *       – Slate:   capture has no GPS coordinates at all (legacy data)
 *   • A side panel listing every capture in chronological order with the
 *     employee photo+name, time, face status, and an Inside / Outside /
 *     GPS unknown badge. Selecting a row pans the map to that marker.
 *
 * Overlapping markers (very close GPS points) are spiderfied onto a
 * small ring so each is individually clickable.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapContainer, TileLayer, Circle, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ShieldCheck, ShieldAlert, MapPin, Clock } from "lucide-react";
import { fmtTime } from "@/lib/utils-app";

function haversineMeters(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

function offsetLatLng(lat, lng, meters, bearingDeg) {
  const R = 6378137;
  const br = (bearingDeg * Math.PI) / 180;
  const dN = meters * Math.cos(br);
  const dE = meters * Math.sin(br);
  const dLat = dN / R;
  const dLng = dE / (R * Math.cos((lat * Math.PI) / 180));
  return [lat + (dLat * 180) / Math.PI, lng + (dLng * 180) / Math.PI];
}

function MapController({ initialBounds, center, zoom }) {
  const map = useMap();
  const didFitRef = useRef(false);
  useEffect(() => {
    if (didFitRef.current) return;
    if (!initialBounds || initialBounds.length === 0) return;
    try {
      if (initialBounds.length === 1) map.setView(initialBounds[0], 15);
      else map.fitBounds(initialBounds, { padding: [40, 40], maxZoom: 17 });
      didFitRef.current = true;
    } catch (e) { /* noop */ }
  }, [initialBounds, map]);
  useEffect(() => {
    if (!center) return;
    map.flyTo(center, zoom ?? Math.max(map.getZoom(), 17), { animate: true, duration: 0.5 });
  }, [center, zoom, map]);
  return null;
}

/**
 * @param {object} props
 * @param {object} props.park           – { id, code, name, latitude, longitude, geofence_radius_m }
 * @param {string} props.date           – yyyy-MM-dd
 * @param {Array}  props.rows           – the same `rows` payload the page already has
 *                                         (each row has .captures: AttendanceRecord[])
 * @param {Function} props.onClose
 */
export default function WorkerGpsTrailDialog({ park, date, rows, onClose }) {
  // Flatten every accepted capture across all employees in chronological order.
  const captures = useMemo(() => {
    const out = [];
    (rows || []).forEach((r) => {
      (r.captures || []).forEach((c) => {
        out.push({
          ...c,
          _employee_code: r.employee_code,
          _employee_name: r.employee_name,
          _designation: r.designation,
        });
      });
    });
    out.sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
    return out;
  }, [rows]);

  const indexed = captures.map((c, i) => ({ ...c, _idx: i + 1 }));

  const [selectedId, setSelectedId] = useState(null);
  const focusRef = useRef(null);
  useEffect(() => {
    const id = setTimeout(() => focusRef.current?.scrollIntoView({ block: "center" }), 60);
    return () => clearTimeout(id);
  }, [selectedId]);

  // Cluster overlapping GPS points so every marker is visible.
  const spiderfied = useMemo(() => {
    const clusters = [];
    indexed.forEach((c) => {
      if (c.gps_lat == null || c.gps_long == null) return;
      const pt = [c.gps_lat, c.gps_long];
      let placed = false;
      for (const cl of clusters) {
        if (haversineMeters(cl.center, pt) <= 15) {
          cl.members.push(c);
          placed = true;
          break;
        }
      }
      if (!placed) clusters.push({ center: pt, members: [c] });
    });
    const out = {};
    clusters.forEach((cl) => {
      if (cl.members.length === 1) {
        const c = cl.members[0];
        out[c.id] = [c.gps_lat, c.gps_long];
      } else {
        const radius = 35;
        cl.members.forEach((c, k) => {
          const bearing = (360 * k) / cl.members.length;
          out[c.id] = offsetLatLng(cl.center[0], cl.center[1], radius, bearing);
        });
      }
    });
    return out;
  }, [indexed]);

  const parkCenter = useMemo(() => (
    park && park.latitude != null && park.longitude != null
      ? [park.latitude, park.longitude]
      : null
  ), [park]);
  const parkRadius = (park?.geofence_radius_m) || 500;

  // Initial bounds: park geofence edge points + every spiderfied marker.
  const initialBounds = useMemo(() => {
    const pts = [];
    if (parkCenter) {
      const dLat = parkRadius / 111320;
      const dLng = parkRadius / (111320 * Math.cos((parkCenter[0] * Math.PI) / 180));
      pts.push([parkCenter[0] + dLat, parkCenter[1] + dLng]);
      pts.push([parkCenter[0] - dLat, parkCenter[1] - dLng]);
    }
    Object.values(spiderfied).forEach((pt) => pts.push(pt));
    return pts;
  }, [parkCenter, parkRadius, spiderfied]);

  const initialCenter = parkCenter || [11.1271, 78.6569]; // TN centroid fallback

  const selectedCapture = indexed.find((c) => c.id === selectedId);
  const panTarget = selectedCapture && spiderfied[selectedCapture.id]
    ? spiderfied[selectedCapture.id]
    : null;

  // Counters for the header summary.
  const insideN = captures.filter((c) => c.inside_geofence === true).length;
  const outsideN = captures.filter((c) => c.inside_geofence === false).length;
  const unknownN = captures.filter((c) => c.gps_lat == null || c.gps_long == null).length;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl p-0" data-testid="worker-gps-trail-dialog">
        <DialogHeader className="px-4 pt-3 pb-2 pr-10">
          <DialogTitle className="flex flex-wrap items-baseline gap-2 text-base pr-2">
            <span className="font-semibold">GPS trail · Worker attendance</span>
            <span className="text-xs font-normal text-muted-foreground">
              {park?.code} · {park?.name} · {date}
            </span>
            <span className="ml-auto flex flex-wrap items-center gap-2 text-[11px] font-normal">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 ring-1 ring-emerald-200">
                <ShieldCheck className="h-3 w-3" /> Inside {insideN}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-rose-700 ring-1 ring-rose-200">
                <ShieldAlert className="h-3 w-3" /> Outside {outsideN}
              </span>
              {unknownN > 0 ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600 ring-1 ring-slate-200">
                  <ShieldAlert className="h-3 w-3" /> No GPS {unknownN}
                </span>
              ) : null}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-0 md:grid-cols-[minmax(0,1fr)_360px]">
          {/* Map column */}
          <div className="relative h-[60vh] min-h-[420px] w-full bg-slate-100 md:rounded-bl-lg">
            <MapContainer
              center={initialCenter}
              zoom={14}
              scrollWheelZoom
              className="h-full w-full md:rounded-bl-lg"
              data-testid="worker-gps-trail-map"
            >
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {parkCenter ? (
                <Circle
                  center={parkCenter}
                  radius={parkRadius}
                  pathOptions={{ color: "#0ea5e9", weight: 1.5, fillColor: "#0ea5e9", fillOpacity: 0.08 }}
                >
                  <Tooltip>
                    <div className="text-xs">
                      <div className="font-semibold text-slate-800">{park.name}</div>
                      <div className="text-muted-foreground">{parkRadius} m geofence</div>
                    </div>
                  </Tooltip>
                </Circle>
              ) : null}
              {indexed.map((c) => {
                if (c.gps_lat == null || c.gps_long == null) return null;
                const isInside = c.inside_geofence === true;
                const isOutside = c.inside_geofence === false;
                const isSelected = c.id === selectedId;
                const color = isInside ? "#059669" : isOutside ? "#e11d48" : "#64748b";
                const icon = L.divIcon({
                  className: "",
                  html: `<div style="
                    background:${color};
                    color:white;
                    width:26px;height:26px;border-radius:50%;
                    display:flex;align-items:center;justify-content:center;
                    font-size:12px;font-weight:600;
                    border:${isSelected ? "3px solid #f59e0b" : "2px solid white"};
                    box-shadow:0 1px 4px rgba(0,0,0,.4);
                  ">${c._idx}</div>`,
                  iconSize: [26, 26],
                  iconAnchor: [13, 13],
                });
                const pos = spiderfied[c.id] || [c.gps_lat, c.gps_long];
                return (
                  <Marker
                    key={c.id}
                    position={pos}
                    icon={icon}
                    eventHandlers={{ click: () => setSelectedId(c.id) }}
                  >
                    <Tooltip direction="top" offset={[0, -10]}>
                      <div className="text-[11px] leading-tight">
                        <div className="font-semibold">#{c._idx} · {c._employee_code} {c._employee_name}</div>
                        <div className="text-muted-foreground">{c.created_at ? fmtTime(c.created_at) : "—"}</div>
                        <div>
                          {isInside ? "Inside geofence" : isOutside ? "Outside geofence" : "GPS unknown"}
                        </div>
                      </div>
                    </Tooltip>
                  </Marker>
                );
              })}
              <MapController initialBounds={initialBounds.length ? initialBounds : null} center={panTarget} />
            </MapContainer>
            {/* Legend */}
            <div className="pointer-events-none absolute bottom-2 left-2 z-[400] rounded-md bg-white/95 px-2 py-1 text-[10px] shadow ring-1 ring-slate-200">
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#0ea5e9", opacity: 0.5 }}></span>
                Park geofence
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#059669" }}></span>
                Inside geofence
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#e11d48" }}></span>
                Outside geofence
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div
            className="flex max-h-[60vh] min-h-[420px] flex-col gap-2 overflow-y-auto border-t border-slate-200 p-2 md:max-h-[60vh] md:border-l md:border-t-0"
            data-testid="worker-gps-trail-list"
          >
            {indexed.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No worker attendance captures recorded on this date.
              </div>
            ) : (
              indexed.map((c) => {
                const isFocus = c.id === selectedId;
                const inside = c.inside_geofence;
                const src = c.photo_b64
                  ? (c.photo_b64.startsWith("data:") ? c.photo_b64 : `data:image/jpeg;base64,${c.photo_b64}`)
                  : null;
                const faceTone = c.face_status === "match"
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : c.face_status === "mismatch"
                    ? "bg-rose-50 text-rose-700 ring-rose-200"
                    : c.face_status === "no_face"
                      ? "bg-amber-50 text-amber-700 ring-amber-200"
                      : "bg-slate-100 text-slate-600 ring-slate-200";
                return (
                  <button
                    type="button"
                    key={c.id}
                    ref={isFocus ? focusRef : null}
                    onClick={() => setSelectedId(c.id)}
                    className={`flex items-stretch gap-2 rounded-md border p-2 text-left transition ${
                      isFocus ? "bg-amber-50/70 ring-2 ring-amber-300" : "bg-white hover:bg-slate-50"
                    }`}
                    data-testid={`worker-gps-row-${c.id}`}
                  >
                    <div className="relative flex-none">
                      {src ? (
                        <img src={src} alt="" className="h-20 w-20 rounded object-cover ring-1 ring-slate-200" />
                      ) : (
                        <div className="grid h-20 w-20 place-items-center rounded bg-slate-100 text-[10px] text-slate-400 ring-1 ring-slate-200">
                          no photo
                        </div>
                      )}
                      <span className="absolute -left-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-slate-900/85 text-[10px] font-semibold text-white ring-1 ring-white">
                        {c._idx}
                      </span>
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="text-sm font-semibold text-slate-800">
                        {c._employee_code} · {c._employee_name}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-slate-600">
                        <Clock className="h-3 w-3 text-slate-500" />
                        <span className="font-mono">{c.created_at ? fmtTime(c.created_at) : "—"}</span>
                        {c._designation ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-[1px] text-[10px] text-slate-700">
                            {c._designation}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-1.5 py-[1px] text-[10px] ring-1 ${faceTone}`}
                        >
                          Face: {c.face_status || "—"}
                          {c.face_match_pct != null ? ` · ${c.face_match_pct}%` : ""}
                        </span>
                        {inside === true ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-[1px] text-[10px] text-emerald-700 ring-1 ring-emerald-200">
                            <ShieldCheck className="h-3 w-3" /> Inside
                          </span>
                        ) : inside === false ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-1.5 py-[1px] text-[10px] text-rose-700 ring-1 ring-rose-200">
                            <ShieldAlert className="h-3 w-3" /> Outside
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-1.5 py-[1px] text-[10px] text-slate-600 ring-1 ring-slate-200">
                            <ShieldAlert className="h-3 w-3" /> GPS unknown
                          </span>
                        )}
                      </div>
                      {c.gps_lat != null && c.gps_long != null ? (
                        <a
                          className="text-[10px] text-sky-700 underline-offset-2 hover:underline"
                          href={`https://maps.google.com/?q=${c.gps_lat},${c.gps_long}`}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MapPin className="mr-0.5 inline h-3 w-3" />
                          {c.gps_lat.toFixed(5)}, {c.gps_long.toFixed(5)}
                        </a>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
